import { useTranslation } from "react-i18next";
import LearningRetry from "../../learning/model/LearningRetry";
import styles from "../css/MarkedTermLearningScreen.module.css";
import { useEffect, useState } from "react";
import { useAppSelector } from "../../../hook";
import { selectTerms } from "../termSlice";
import { toDecimalString } from "../../common/strings";
import { NO_LIMIT, getLearningTypeText } from "../../learning/util/learning";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import Group from "../../groups/Group";
import { getFilteredTermsWithOptions } from "../terms";
import { makeSelectedGroupsString } from "../../groups/groups";
import ListSettingView from "../../common/ListSettingView";
import { LearningType } from "../../learning/model/LearningType";
import BigButton from "../../common/BigButton";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import CountPicker from "../../common/CountPicker";
import { CountPickerStyle } from "../../common/CountPickerStyle";
import BasicMenu from "../../common/BasicMenu";
import { getSelectedGroupsFromLocalStorage } from "../../settings/util/termListSettings";
import Term from "../Term";
import {
  getLearningIntervalUnitCountFromLocalStorage,
  setLearningIntervalUnitCountToLocalStroage,
  setLearningReviewLatestType,
} from "../../settings/util/learningSettings";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface MarkedTermLearningScreenProps {
  open: boolean;
  onClickStartButton: (data: LearningRetry) => void;
  onClose: () => void;
}

export default function MarkedTermLearningScreen(
  props: MarkedTermLearningScreenProps
) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const rawTerms = useAppSelector(selectTerms);
  const [termList, setTermList] = useState<Term[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

  const [selectedGroupName, setSelectedGroupName] = useState<string>("");

  const [parts, setParts] = useState<string[]>([]);
  const [termCount, setTermCount] = useState<number>(0);
  const [intervalUnitCountString, setIntervalUnitCountString] = useState("");

  const [openIntervalUnitCountPicker, setOpenIntervalUnitCountPicker] =
    useState<boolean>(false);
  const [openGroupSelectScreen, setOpenGroupSelectScreen] =
    useState<boolean>(false);
  const [openLearningType, setOpenLearningType] = useState<boolean>(false);

  const [currentSelectedLearningType, setCurrentSelectedLearningType] =
    useState<LearningType>(LearningType.spellingQuiz);

  useEffect(() => {
    updateIntervalUnitCountString();
    setSelectedGroups(getSelectedGroupsFromLocalStorage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    updateTermList(selectedGroups);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawTerms]);

  useEffect(() => {
    const groupName = makeSelectedGroupsString(selectedGroups);
    setSelectedGroupName(groupName);
    updateTermList(selectedGroups);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroups]);

  useEffect(() => {
    const count = termList.length;
    const localized = t("LearningReviewTermCountText");
    const parts = localized.split("{{count}}");
    setTermCount(count);
    setParts(parts);
  }, [t, termList]);

  function updateTermList(groups: Group[]) {
    const newList = getFilteredTermsWithOptions(
      rawTerms,
      undefined,
      undefined,
      groups,
      undefined,
      true
    );

    setTermList(newList);
  }

  function updateSelectedGroup(groups: Group[]) {
    setSelectedGroups(groups);
  }

  function updateIntervalUnitCount(count: number) {
    setLearningIntervalUnitCountToLocalStroage(count);
    updateIntervalUnitCountString();
  }

  function updateIntervalUnitCountString() {
    const countValue = getLearningIntervalUnitCountFromLocalStorage();
    const countDecimalString = toDecimalString(countValue);
    let string = "";
    if (Number(countValue) === NO_LIMIT) {
      string = t("LearningIntervalUnitCountAllType");
    } else {
      string = t("LearningIntervalUnitCountValueType", {
        value: countDecimalString,
      });
    }
    setIntervalUnitCountString(string);
  }

  function handleOnClickGroupSelectButton(e: React.MouseEvent<HTMLDivElement>) {
    setAnchorEl(e.currentTarget);
    setOpenGroupSelectScreen(true);
  }

  function handleOnClickIntervalUnitCountButton(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenIntervalUnitCountPicker(true);
  }

  function handleOnClickLearningTypeSettingView(
    e: React.MouseEvent<HTMLDivElement>
  ) {
    setAnchorEl(e.currentTarget);
    setOpenLearningType(true);
  }

  if (props.open === false) return null;
  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div className={styles.contentContainer}>
            <div className={styles.contentsContainer}>
              <ContainerNavigationView
                open={true}
                title={t("ShowMarkedTermMenuTitle")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={() => {
                  props.onClose();
                }}
              />

              <div className={styles.reviewTermCountContainer}>
                <span className={styles.reviewTermCountText}>
                  {parts[0]}
                  <span className={styles.reviewTermCountHighlightText}>
                    {termCount}
                  </span>
                  {parts[1]}
                </span>
              </div>

              <div className={styles.settingContainer}>
                <ListSettingView
                  title={t("GroupSelectToLearn")}
                  value={selectedGroupName}
                  valueStyle="stringValue"
                  handleOnClick={handleOnClickGroupSelectButton}
                  showingDivider={true}
                />
                <ListSettingView
                  title={t("LearningIntervalUnitCountSettingTitle")}
                  description={t("QuestionPerSectionCountSettingTitle")}
                  value={intervalUnitCountString}
                  valueStyle="stringValue"
                  handleOnClick={handleOnClickIntervalUnitCountButton}
                />
              </div>

              <div className={styles.settingContainer}>
                <ListSettingView
                  title={t("LearningReviewModeTitle")}
                  value={getLearningTypeText(currentSelectedLearningType)}
                  valueStyle="stringValue"
                  showingDivider={true}
                  handleOnClick={handleOnClickLearningTypeSettingView}
                />
              </div>

              <BigButton
                title={t("Start")}
                variant={termList.length === 0 ? "disable" : "normal"}
                handleClick={() => {
                  if (termList.length === 0) return;
                  const item = new LearningRetry(
                    termList,
                    currentSelectedLearningType
                  );
                  props.onClickStartButton(item);
                  props.onClose();
                }}
              />
            </div>
          </div>
        </div>
        <GroupSelectScreen
          open={openGroupSelectScreen === true}
          selectStyle="multiple"
          showAllGroup={true}
          showNoGroup={true}
          showGroupAddButton={false}
          selectedGroups={selectedGroups.map((group) => group.title)}
          handleOnSelectGroups={(groups: Group[]) => {
            updateSelectedGroup(groups);
          }}
          handleOnClose={() => {
            setOpenGroupSelectScreen(false);
          }}
        />
        <CountPicker
          open={openIntervalUnitCountPicker}
          defaultValue={getLearningIntervalUnitCountFromLocalStorage()}
          style={CountPickerStyle.learningIntervalUnitCount}
          onComplete={(count: number) => updateIntervalUnitCount(count)}
          onClose={() => {
            setOpenIntervalUnitCountPicker(false);
          }}
        />
        <BasicMenu
          isOpen={openLearningType}
          anchorEl={anchorEl}
          buttons={[
            {
              title: t("Flashcard"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.flashcards);
                setLearningReviewLatestType(LearningType.flashcards);
              },
            },
            {
              title: t("MultipleChoiceTitle"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.multiplechoice);
                setLearningReviewLatestType(LearningType.multiplechoice);
              },
            },
            {
              title: t("SpellingQuiz"),
              handleOnClose: () => {
                setOpenLearningType(false);
                setCurrentSelectedLearningType(LearningType.spellingQuiz);
                setLearningReviewLatestType(LearningType.spellingQuiz);
              },
            },
          ]}
          handleOnClose={() => {
            setOpenLearningType(false);
          }}
        />
      </div>
    </>
  );
}
